"use client";

import * as React from "react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
function DownloadCtaConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [scrollY, setScrollY] = React.useState(0);
  React.useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (loading) {
    return <section id="download-cta" className="section-dark relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
            <div className="aspect-[3/4] rounded-lg bg-white/10 animate-pulse" />
            <div className="space-y-4">
              <div className="h-12 bg-white/10 rounded animate-pulse" />
              <div className="h-24 bg-white/10 rounded animate-pulse" />
              <div className="h-12 bg-white/10 rounded animate-pulse w-3/4" />
            </div>
          </div>
        </div>
      </section>;
  }
  if (error || products.length === 0) {
    return <section id="download-cta" className="section-dark relative overflow-hidden">
        <div className="container mx-auto px-4 text-center">
          <p className="text-dark-foreground/70">Unable to load product information.</p>
        </div>
      </section>;
  }
  const product = products[0];
  const parallaxOffset = scrollY * 0.05;
  return <section id="download-cta" className="section-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
    }} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center max-w-6xl mx-auto">
          <div className="relative order-2 lg:order-1" style={{
          transform: `translateY(${parallaxOffset}px)`,
          transition: 'transform 0.1s linear'
        }}>
            <div className="relative aspect-[3/4] max-w-md mx-auto lg:mx-0">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-lg blur-2xl" />
              <img src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542868/img/survival_kit_pdf_mockup.png"} alt={product.title || "Free Survival Kit PDF"} className="relative w-full h-full object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.4)] rounded-lg" loading="eager" />
            </div>
          </div>

          <div className="order-1 lg:order-2 space-y-6 text-center lg:text-left">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-dark-foreground leading-tight tracking-tight">
                {product.title || "Get Your Free Survival Kit"}
              </h2>
              
              {product.short_description && <div className="text-lg md:text-xl text-dark-foreground/80 leading-relaxed prose prose-invert max-w-none" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}
            </div>

            <div className="pt-4">
              <AddToCartButton productId={product.id} variant="default" size="lg" className="w-full" buttonClassName="bg-accent text-foreground-accent hover:bg-accent-hover hover:scale-[1.03] active:scale-100 transition-all duration-fast ease-spring px-8 py-6 text-lg font-bold rounded-[var(--radius-sm)] shadow-strong" text={<span className="flex items-center justify-center gap-3">
                    <Download className="w-6 h-6" />
                    Download Now
                  </span>} showQuantity={false} />
            </div>

            {product.customFields?.file_size && <p className="text-sm text-dark-foreground/60 text-center lg:text-left">
                PDF • {product.customFields.file_size} • Instant Access
              </p>}
          </div>
        </div>
      </div>
    </section>;
}
export default function DownloadCta() {
  return <EcommerceProductsProvider postType="product" wp_query={{
    per_page: 1,
    product_tag: [21],
    orderby: "date",
    order: "desc"
  }} fields={["id", "title.rendered", "short_description", "featured_image_url", "link", "meta"]}>
      <DownloadCtaConsumer />
    </EcommerceProductsProvider>;
}